import React, {lazy, Suspense, useEffect, useRef, useState} from "react";
import {WikiPage, WikiPageRestControllerApiFactory} from "arteco-api-client-ts";
import {
	Alert,
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	IconButton,
	Stack,
	Tooltip,
	Typography,
	LinearProgress,
} from "@mui/material";
import {useNavigate} from "react-router-dom";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import {useSnackbar} from "notistack";
import {FormContext} from "../../../../components/form/form_inputs/form_buttons";
import {FormInput} from "../../../../components/form/form_inputs/form_input";
import FormFile from "../../../../components/form/form_inputs/form_file";
import FormToggle from "../../../../components/form/form_inputs/form_toggle";
import {useForm, UseFormProps} from "../../../../components/form/form_model";
import {useApiManager} from "../../../../components/utils/api";
import {useAuthManager} from "../../../../components/utils/auth";
import {dateToString} from "../../../../components/utils/dates";
import {btnSx, Page, PageHeader, Panel, relative, SkeletonRows} from "../../../../components/kit";
import {SECTION_LABELS, wikiAdminEditPath, wikiAdminListPath, wikiReaderPath} from "../../../../components/wiki/sections";

// El editor visual pesa: se carga solo al abrir una página para editarla (#14293).
const WikiContentEditor = lazy(() => import("../../../../components/wiki/visual_editor").then(m => ({default: m.WikiContentEditor})));

export {SECTION_HR, SECTION_RECRUITMENT, SECTION_BILLING, SECTION_SYSTEM, SECTION_ACCOUNTING, SECTION_MARKETING, SECTION_COMPLIANCE} from "../../../../components/wiki/sections";

export interface WikiPageEditPageProps {
	section: string;
}

const TRACKED_FIELDS = ["title", "code", "linkA", "linkB", "linkC"];

interface Draft {
	content: string;
	at: string;
}

const draftKey = (section: string, id?: number) => `wiki-draft:${section}:${id ?? "new"}`;
const readDraft = (key: string): Draft | undefined => {
	try {
		const raw = localStorage.getItem(key);
		return raw ? JSON.parse(raw) as Draft : undefined;
	} catch {
		return undefined;
	}
};
const writeDraft = (key: string, draft?: Draft) => {
	try {
		if (draft) localStorage.setItem(key, JSON.stringify(draft));
		else localStorage.removeItem(key);
	} catch {
		// sin almacenamiento local (modo privado): el borrador es solo una ayuda
	}
};

export const WikiPageEditPage = ({section}: WikiPageEditPageProps) => {
	const authManager = useAuthManager();
	const apiManager = useApiManager();
	const navigate = useNavigate();
	const {enqueueSnackbar} = useSnackbar();
	const ctrl = apiManager.factory(WikiPageRestControllerApiFactory);

	const [content, setContent] = useState<string>("");
	const contentRef = useRef("");
	contentRef.current = content;
	const [dirty, setDirty] = useState(false);
	const [saving, setSaving] = useState(false);
	const [confirmDelete, setConfirmDelete] = useState(false);
	const [draft, setDraft] = useState<Draft>();

	const form = useForm({
		fetcher: (id) => ctrl.getWikiPage(id),
		updater: (bean) => ctrl.saveWikiPage(bean),
		remover: (id) => ctrl.deleteOrDismissWikiPage(id),
		postLoad: (bean) => {
			setContent(bean.content || "");
			const saved = readDraft(draftKey(section, bean.id));
			setDraft(saved && saved.content !== (bean.content || "") ? saved : undefined);
			setDirty(false);
		},
		factory: () => ({
			modifier: {id: authManager.getUserId()},
			section: section,
			visible: false,
		} as WikiPage),
	} as UseFormProps<WikiPage>);

	const bean = form.propModel.value;
	const id = bean?.id;

	// El contenido lo gestiona el editor propio: se registra como un campo más del formulario.
	useEffect(() => {
		form.register("content", () => contentRef.current, v => setContent(v ?? ""));
		return () => form.unregister("content");
	}, []);

	useEffect(() => {
		const offs = TRACKED_FIELDS.map(f => form.onFieldChange(f, () => setDirty(true)));
		return () => offs.forEach(off => off());
	}, []);

	const isDirty = dirty;
	useEffect(() => {
		if (!isDirty) return;
		const warn = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ""; };
		window.addEventListener("beforeunload", warn);
		return () => window.removeEventListener("beforeunload", warn);
	}, [isDirty]);

	const changeContent = (v: string) => {
		setContent(v);
		setDirty(true);
		writeDraft(draftKey(section, id), {content: v, at: new Date().toISOString()});
	};

	const save = () => {
		if (saving) return;
		setSaving(true);
		const wasNew = !id;
		form.save().then(saved => {
			writeDraft(draftKey(section, id));
			setDraft(undefined);
			setDirty(false);
			if (wasNew && saved?.id) navigate(wikiAdminEditPath(section, saved.id), {replace: true});
		}).catch(() => undefined).finally(() => setSaving(false));
	};

	const remove = () => {
		if (!id) return;
		apiManager.execute(ctrl.deleteOrDismissWikiPage(id)).then(() => {
			writeDraft(draftKey(section, id));
			enqueueSnackbar("Página eliminada", {variant: "success"});
			navigate(wikiAdminListPath(section));
		});
	};

	const code = (bean?.code || "").trim();
	const readerPath = code ? wikiReaderPath(section, code) : undefined;
	const updated = bean?.modified || bean?.created;

	const subtitle = form.isLoading() ? "Cargando…"
		: isDirty ? "Cambios sin guardar"
			: id ? `Guardada${updated ? ` · modificada ${relative(updated)}` : ""}${bean?.modifier?.username ? ` por ${bean.modifier.username}` : ""}`
				: "Página nueva";

	return (
		<Page maxWidth={1440}>
			<PageHeader
				back={wikiAdminListPath(section)}
				title={bean?.title || (id ? "Página de la wiki" : "Nueva página")}
				subtitle={<Box component="span" sx={{color: isDirty ? "warning.dark" : undefined, fontWeight: isDirty ? 600 : undefined}}>{subtitle}</Box>}
				actions={
					<Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
						{id && (
							<Button color="error" startIcon={<DeleteOutlineRoundedIcon/>} onClick={() => setConfirmDelete(true)} sx={btnSx}>
								Eliminar
							</Button>
						)}
						{id && readerPath && bean?.visible && (
							<Button variant="outlined" startIcon={<VisibilityOutlinedIcon/>} href={readerPath} target="_blank" sx={btnSx}>
								Ver como empleado
							</Button>
						)}
						<Button variant="contained" onClick={save} disabled={saving || form.isLoading() || (!!id && !isDirty)} sx={btnSx}>
							{saving ? "Guardando…" : "Guardar página"}
						</Button>
					</Stack>
				}
			/>

			{form.isLoading() ? (
				<Panel><SkeletonRows rows={6}/></Panel>
			) : (
				<form id={form.formId} onSubmit={e => e.preventDefault()}>
					<FormContext.Provider value={{form}}>
						{draft && (
							<Alert severity="info" sx={{mb: 2, borderRadius: 2}}
								   action={
									   <Stack direction="row" spacing={1}>
										   <Button color="inherit" size="small" sx={btnSx}
												   onClick={() => { setContent(draft.content); setDirty(true); setDraft(undefined); }}>
											   Recuperar
										   </Button>
										   <Button color="inherit" size="small" sx={btnSx}
												   onClick={() => { writeDraft(draftKey(section, id)); setDraft(undefined); }}>
											   Descartar
										   </Button>
									   </Stack>
								   }>
								Hay un borrador sin guardar de este navegador, del {dateToString(draft.at, "DD/MM/YYYY HH:mm")}.
							</Alert>
						)}

						{/* El editor ocupa todo el ancho; «Publicación» y «Recursos» van debajo, en una fila (#14313). */}
						<Stack spacing={2} sx={{minWidth: 0}}>
							<Panel sx={{p: {xs: 2, sm: 2.5}}}>
								<FormInput field="title" type="text" label="Título"
										   inputProps={{style: {fontSize: 20, fontWeight: 700}}}/>
							</Panel>
							<Suspense fallback={<LinearProgress/>}>
								<WikiContentEditor value={content} onChange={changeContent} section={section} onSave={save}/>
							</Suspense>

							<Box sx={{
								display: "grid", gap: 2, alignItems: "start",
								gridTemplateColumns: {xs: "minmax(0, 1fr)", md: "minmax(0, 1fr) minmax(0, 2fr)"},
							}}>
								<Panel title="Publicación" subtitle={`Wiki de ${SECTION_LABELS[section] || section}`}>
									<Stack spacing={2}>
										<Stack direction="row" spacing={1} alignItems="flex-start">
											<Box sx={{flex: 1, minWidth: 0}}>
												<FormInput field="code" type="text" label="Código"
														   helperText="Identifica la página para enlazarla y para leerla en el portal del empleado."/>
											</Box>
											{readerPath && (
												<Tooltip title="Copiar el enlace para usarlo en otra página">
													<IconButton sx={{mt: 1}} aria-label="Copiar enlace"
																onClick={() => navigator.clipboard?.writeText(readerPath)
																	.then(() => enqueueSnackbar("Enlace copiado", {variant: "success"}))
																	.catch(() => enqueueSnackbar(readerPath, {variant: "info"}))}>
														<ContentCopyRoundedIcon fontSize="small"/>
													</IconButton>
												</Tooltip>
											)}
										</Stack>
										<Box onClickCapture={() => setDirty(true)}>
											<FormToggle field="visible" label="Visible para los empleados"/>
										</Box>
										{updated && (
											<Typography variant="caption" color="text.secondary">
												Creada el {dateToString(bean?.created)} · modificada el {dateToString(updated)}
												{id ? ` · nº ${id}` : ""}
											</Typography>
										)}
									</Stack>
								</Panel>

								<Panel title="Recursos" subtitle="Enlaces y archivos que acompañan a la página.">
									<Box sx={{display: "grid", gap: 2, gridTemplateColumns: {xs: "minmax(0, 1fr)", sm: "repeat(2, minmax(0, 1fr))"}}}>
										<Stack spacing={1.5} sx={{minWidth: 0}}>
											<FormInput field="linkA" type="text" label="Enlace 1" placeholder="https://"/>
											<FormInput field="linkB" type="text" label="Enlace 2" placeholder="https://"/>
											<FormInput field="linkC" type="text" label="Enlace 3" placeholder="https://"/>
										</Stack>
										<Stack spacing={1.5} sx={{minWidth: 0}}>
											{[1, 2, 3].map(n => (
												<FormFile key={n} field={`attachment${"ABC"[n - 1]}`} label={`Archivo ${n}`}
														  onChange={() => setDirty(true)}
														  onDownload={() => apiManager.executeRaw(ctrl.downloadAttachmentWikiPage(form.getIdOrErr(), n))}/>
											))}
										</Stack>
									</Box>
								</Panel>
							</Box>
						</Stack>
					</FormContext.Provider>
				</form>
			)}
			<Box id={form.formId + "_errs"}/>

			<Dialog open={confirmDelete} onClose={() => setConfirmDelete(false)}>
				<DialogTitle fontWeight={700}>¿Eliminar esta página?</DialogTitle>
				<DialogContent>
					<DialogContentText>
						«{bean?.title}» dejará de verse en el portal del empleado y los enlaces que apunten a ella dejarán de funcionar.
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setConfirmDelete(false)} sx={btnSx}>Cancelar</Button>
					<Button color="error" variant="contained" onClick={() => { setConfirmDelete(false); remove(); }} sx={btnSx}>
						Eliminar página
					</Button>
				</DialogActions>
			</Dialog>
		</Page>
	);
};
